<script>
  import InfoHint from './InfoHint.svelte';
  import { t } from '../i18n/index.js';
  import { describeValue } from '../lib/bilancio.js';

  /**
   * Un valore del bilancio (un indice, o una cifra del riscontro) con quello
   * su cui poggia. Un valore che non è certo non è mai scritto come un dato:
   * accanto ha sempre il badge «stima» o «ipotesi», il margine o l'intervallo,
   * e la spiegazione della base a portata di tocco. Senza valore, «n.d.» con
   * il motivo.
   *
   * Le frasi delle basi e dei motivi arrivano dal server (`testi.basi`,
   * `testi.motivi`), non sono copiate qui.
   */

  /** `{ value, basis, interval?, tolerance?, reason? }` come lo manda l'API. */
  export let result = null;
  /** '€' | '%' | 'x' | 'giorni' */
  export let unit = '€';
  /** `{ basi, motivi }`: le frasi del server per le basi e i motivi. */
  export let basi = {};
  export let motivi = {};
  /** Allineato a sinistra, per le celle che non sono colonne di cifre. */
  export let inline = false;
  /** Una variazione: «+» davanti ai valori positivi. */
  export let signed = false;

  $: d = describeValue(result, unit, { signed });
  $: basisText = result?.basis ? (basi?.[result.basis] ?? '') : '';
  $: reasonText = d.reason ? (motivi?.[d.reason] ?? '') : '';
  $: detail = [
    d.plusMinus ? $t('bilancio.basi.plusMinus', { value: d.plusMinus }) : '',
    d.range ? $t('bilancio.basi.range', { min: d.range.min, max: d.range.max }) : '',
  ].filter(Boolean).join(' · ');
</script>

<span class="bilancio-value" class:bilancio-value-inline={inline}>
  {#if d.kind === 'nd'}
    <span class="bilancio-value-line">
      <span class="bilancio-value-nd" title={$t('bilancio.basi.ndLabel')}>{$t('bilancio.basi.nd')}</span>
      <InfoHint text={reasonText || $t('bilancio.basi.ndLabel')} />
    </span>
  {:else}
    <span class="bilancio-value-line">
      <span class="bilancio-value-figure" class:bilancio-value-uncertain={d.kind !== 'certo'}>
        {#if d.kind === 'stima'}<span class="sr-only">{$t('bilancio.basi.srStima')} </span>{/if}
        {#if d.kind === 'ipotesi'}<span class="sr-only">{$t('bilancio.basi.srIpotesi')} </span>{/if}
        {d.text}
      </span>
      {#if d.kind === 'stima'}
        <span class="badge badge-blue">{$t('bilancio.basi.badge.stima')}</span>
      {:else if d.kind === 'ipotesi'}
        <span class="badge badge-yellow">{$t('bilancio.basi.badge.ipotesi')}</span>
      {/if}
      {#if d.kind !== 'certo' && basisText}<InfoHint text={basisText} />{/if}
    </span>
    {#if d.plusMinus || d.range}
      <span class="bilancio-value-detail">{detail}</span>
    {/if}
  {/if}
</span>

<style>
  .bilancio-value {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    max-width: 100%;
  }
  .bilancio-value-inline { align-items: flex-start; }
  .bilancio-value-line {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 4px 6px;
  }
  .bilancio-value-figure {
    color: var(--color-text-heading);
    font-variant-numeric: var(--wd-figures-tabular);
    white-space: nowrap;
  }
  .bilancio-value-uncertain { color: var(--color-text-body); }
  .bilancio-value-nd { color: var(--color-text-muted); }
  .bilancio-value-detail {
    color: var(--color-text-muted);
    font-size: 0.75rem;
    font-variant-numeric: var(--wd-figures-tabular);
    text-align: right;
  }
  .bilancio-value-inline .bilancio-value-detail { text-align: left; }
  @media (width < 42.5rem) {
    .bilancio-value { align-items: flex-start; }
    .bilancio-value-line { justify-content: flex-start; }
    .bilancio-value-detail { text-align: left; }
  }
</style>
